/**
 * Hero headline reveal — Figma "load in text" prototype (node 1272:3296).
 *
 * The headline arrives as a shared heading_block, so the split into one clip box
 * per visual line happens at runtime: only the browser knows where it broke.
 */

const REVEAL = {
  duration: 0.9,
  stagger: 0.12,
  // The curve MotionEffect uses for its scroll-in effects.
  easing: [0.22, 0.61, 0.36, 1],
  inViewMargin: '0px 0px -80px 0px',
  // Enough to clear the padded clip box, so descenders never peek out at rest.
  hiddenOffset: '110%',
  fontTimeout: 2000,
};

const HEADING_SELECTOR = '.block__heading :is(h1, h2, h3, h4, h5, h6)';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Line breaks measured before the webfont lands are the fallback font's breaks,
 * so wait for fonts — but never hang, since the headline is hidden until this
 * resolves.
 */
function fontsReady() {
  if (!document.fonts || !document.fonts.ready) return Promise.resolve();
  return Promise.race([
    document.fonts.ready,
    new Promise((resolve) => setTimeout(resolve, REVEAL.fontTimeout)),
  ]);
}

class HeroReveal extends HTMLElement {
  async connectedCallback() {
    this.heading = this.querySelector(HEADING_SELECTOR);

    // The CSS gate hides the headline from first paint, so every exit path
    // below has to reach #ready().
    if (
      !this.heading ||
      typeof Motion === 'undefined' ||
      prefersReducedMotion()
    ) {
      this.#ready();
      return;
    }

    this.originalHTML = this.heading.innerHTML;

    try {
      await fontsReady();
      if (!this.isConnected) return;
      this.#split();
      // Offset the lines while the headline is still gated — releasing first
      // would expose a frame of the resting position.
      this.#setInitialState();
    } catch (error) {
      this.heading.innerHTML = this.originalHTML;
      this.lines = [];
    } finally {
      this.#ready();
    }

    if (!this.lines || !this.lines.length) return;

    this.#watchResize();
    Motion.inView(this, () => this.#reveal(), { margin: REVEAL.inViewMargin });
  }

  #ready() {
    this.setAttribute('data-reveal-ready', '');
  }

  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
  }

  /**
   * Rebuild the heading as one clip box per visual line. A heading carrying
   * markup is wrapped whole instead, since rebuilding it from words would drop
   * that markup.
   */
  #split() {
    this.lines = [];

    if (this.heading.children.length > 0) {
      this.lines = [this.#wrapLine(this.heading.childNodes)];
      return;
    }

    const words = this.heading.textContent.replace(/\s+/g, ' ').trim().split(' ');
    if (!words[0]) return;

    const probeFragment = document.createDocumentFragment();
    const probes = words.map((word, index) => {
      const probe = document.createElement('span');
      probe.className = 'hero-reveal__word';
      probe.textContent = word;
      probeFragment.appendChild(probe);
      if (index < words.length - 1) {
        probeFragment.appendChild(document.createTextNode(' '));
      }
      return probe;
    });
    this.heading.replaceChildren(probeFragment);

    // Group words by the top edge they landed on. One layout pass, so
    // viewport-relative tops are safe to compare directly.
    const grouped = [];
    let previousTop = null;
    probes.forEach((probe, index) => {
      const top = probe.getBoundingClientRect().top;
      if (previousTop === null || Math.abs(top - previousTop) > 1) {
        grouped.push([]);
        previousTop = top;
      }
      grouped[grouped.length - 1].push(words[index]);
    });

    const lineFragment = document.createDocumentFragment();
    this.lines = grouped.map((lineWords) => {
      const { line, inner } = this.#buildLine();
      inner.textContent = lineWords.join(' ');
      lineFragment.appendChild(line);
      return inner;
    });
    this.heading.replaceChildren(lineFragment);
  }

  #wrapLine(nodes) {
    const { line, inner } = this.#buildLine();
    inner.append(...nodes);
    this.heading.replaceChildren(line);
    return inner;
  }

  #buildLine() {
    const line = document.createElement('span');
    line.className = 'hero-reveal__line';
    const inner = document.createElement('span');
    inner.className = 'hero-reveal__line-inner';
    line.appendChild(inner);
    return { line, inner };
  }

  /**
   * Plain inline styles, not `Motion.animate(..., { duration: 0 })`: Motion
   * resolves keyframes on its own frame loop, so a zero-duration animation
   * lands a frame late, which an above-the-fold hero is quick enough to paint.
   */
  #setInitialState() {
    this.lines.forEach((inner) => {
      inner.style.willChange = 'transform';
      inner.style.transform = `translateY(${REVEAL.hiddenOffset})`;
    });
  }

  #reveal() {
    if (this.revealed) return;
    this.revealed = true;

    this.lines.forEach((inner, index) => {
      Motion.animate(
        inner,
        // Both ends explicit, so Motion never has to read the resting
        // transform back off the element.
        {
          transform: [`translateY(${REVEAL.hiddenOffset})`, 'translateY(0%)'],
        },
        {
          duration: REVEAL.duration,
          delay: index * REVEAL.stagger,
          easing: REVEAL.easing,
        }
      ).finished.then(() => {
        inner.style.willChange = '';
      });
    });
  }

  /**
   * Re-split on width change only — a height-only resize is the mobile URL bar,
   * not a reflow.
   */
  #watchResize() {
    let lastWidth = window.innerWidth;

    this.onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.heading.innerHTML = this.originalHTML;
        this.#split();
        if (!this.revealed) this.#setInitialState();
      }, 150);
    };

    window.addEventListener('resize', this.onResize);
  }
}

if (!customElements.get('hero-reveal')) {
  customElements.define('hero-reveal', HeroReveal);
}
